import React, { useEffect, useState } from 'react'
import { useAppDispatch } from '../../../../app/hooks'
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, Link, TextField, Typography } from '@mui/material'
import { Formik } from 'formik'
import { useTwoFAApiEnableTwoFA, useTwoFAApiGenerateSecretMutation } from '../../../../api/two-fa/twoFAApi'
import LoadingComponent from '../../../shared/components/ui-widgets/LoadingComponent'
import { type GoogleAuthentication } from '../../../../api/schema-dtos/LegacyTwoFADomainDtos'
import { setNotification } from '../../../shared/components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../../api/shared/RtkQueryHelper'
import { type EnableTwoFAPayload } from '../../../../api/schema-dtos/LegacyTwoFAPayloadDtos'
import ButtonLoadingComponent from '../../../shared/components/ui-widgets/ButtonLoadingComponent'
import CopyToClipboardComponent from '../../../shared/components/ui-widgets/CopyToClipboardComponent'

interface TwoFaSetupComponentProps {
  onSuccess: () => void
}

const TwoFaSetupComponent: React.FC<TwoFaSetupComponentProps> = (props: TwoFaSetupComponentProps) => {
  const { onSuccess } = props

  const dispatch = useAppDispatch()
  const [openDialog, toggleDialog] = useState<boolean>(false)
  const [generatedSecret, setGeneratedSecret] = useState<GoogleAuthentication | null>(null)

  const defaultForm: EnableTwoFAPayload = {
    token: ''
  }

  const [apiGenerateSecret, generateSecretResult] = useTwoFAApiGenerateSecretMutation()
  const [apiEnableTwoFA, enableTwoFaResult] = useTwoFAApiEnableTwoFA()

  const handleGenerateSecret = (): void => {
    apiGenerateSecret()
      .unwrap()
      .then(response => {
        setGeneratedSecret(response)
      })
      .catch((error) => {
        dispatch(setNotification({
          open: true,
          severity: 'error',
          message: RtkQueryHelper.mapResponseError(error)
        }))
      })
  }

  const handleEnableTwoFA = (payload: EnableTwoFAPayload): void => {
    apiEnableTwoFA(payload)
      .unwrap()
      .then(() => {
        onSuccess()
        dispatch(setNotification({
          open: true,
          severity: 'success',
          message: 'Your Two-Factor Authentication has been enabled.'
        }))
      })
      .catch(error => {
        dispatch(setNotification({
          open: true,
          severity: 'error',
          message: RtkQueryHelper.mapResponseError(error)
        }))
      })
  }

  useEffect(() => {
    if (generatedSecret === null && !generateSecretResult.isLoading && !generateSecretResult.isSuccess && !generateSecretResult.isError) {
      handleGenerateSecret()
    }
  }, [generatedSecret, generateSecretResult])

  return (
    <>
      <Button
        variant={'contained'}
        color={'primary'}
        onClick={() => {
          toggleDialog(true)
        }}
      >
        Level-up Account Security
      </Button>

      <Dialog
        open={openDialog}
        maxWidth={'lg'}
        fullWidth={true}
        disableEscapeKeyDown={true}
        onClose={() => {
          toggleDialog(false)
        }}
        sx={{ p: 2 }}
      >
        <DialogTitle>Level-up account security</DialogTitle>

        <DialogContent>
          <Grid container spacing={2}>
            <Grid item md={6}>
              <Typography variant={'body1'} sx={{ mb: 2 }}>
                Install a two-factor authenticator app like Authy, Google
                Authenticator or Microsoft Authenticator to level-up your account security:
              </Typography>
              <ul className="">
                <li className="mb-2">
                  <Typography variant={'body1'}>
                    For <b>Android</b>, you can get it from the <Link target="_blank"
                                                                      href="https://play.google.com/store/search?q=authenticator&c=apps&hl=en">Google
                    Play Store</Link>
                  </Typography>
                </li>
                <li>
                  <Typography variant={'body1'}>
                    For <b>iOS (iPhone, iPad)</b> you can install it from the <Link target="_blank"
                                                                                    href="https://www.apple.com/za/search/authenticator?src=globalnav"
                                                                                    rel="noreferrer">App Store</Link>
                  </Typography>
                </li>
                <li>
                  <Typography variant={'body1'}>
                    Once installed, scan the QR code with the app and enter the secret token the app provides.
                  </Typography>
                </li>
              </ul>
            </Grid>
            <Grid item md={6}>
              {generateSecretResult.isLoading && <LoadingComponent/>}
              {generatedSecret !== null &&
                <>
                  <Box sx={{ textAlign: 'center' }}>
                    <img src={generatedSecret.qrCode} alt={'Two-FA QR Code'} width={250}/>

                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                      <CopyToClipboardComponent
                        textToCopy={generatedSecret.secret}
                        buttonText={generatedSecret.secret}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ my: 3 }}>
                    <Typography variant={'h5'}>Back up your secret key</Typography>
                    <Typography variant={'body1'}>Resetting your two-factor authentication requires you to contact us
                      and may take up to 48 hours to address.</Typography>

                    <Box sx={{ mt: 2 }}>
                      <Formik
                        initialValues={defaultForm}
                        onSubmit={(values) => {
                          handleEnableTwoFA(values)
                        }}
                      >
                        {formikProps => (
                          <form
                            autoComplete={'off'}
                            autoCorrect={'off'}
                            onSubmit={formikProps.handleSubmit}
                          >
                            <TextField
                              label={'Secret Token'}
                              type={'number'}
                              variant="outlined"
                              sx={{ width: '100%', mb: 2 }}
                              placeholder={'Secret Token'}
                              {...formikProps.getFieldProps('token')}
                            />
                            <Button
                              variant={'contained'}
                              type="submit"
                              sx={{ mr: 2 }}>
                              Enable Two-Factor Authentication
                              {(enableTwoFaResult.isLoading) && <ButtonLoadingComponent/>}
                            </Button>
                            <Button
                              variant={'outlined'}
                              type="button"
                              onClick={() => {
                                toggleDialog(false)
                              }}
                            >
                              Cancel
                            </Button>
                          </form>
                        )}
                      </Formik>
                    </Box>
                  </Box>
                </>
              }
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default TwoFaSetupComponent
